import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatTooltipModule } from '@angular/material/tooltip';
import { finalize, Subject, takeUntil, Observable, map, forkJoin, tap } from 'rxjs';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { RefreshSpinDirective } from '@olv/ui-shared/directives/refresh-spin.directive';
import { NetworkMetadataService } from '@olv/ui-shared/services/network-metadata.service';
import { RiskLevelEnum } from '@olv/ui-shared/enums/task.enums';
import {
  TransactionStatusEnum,
  TransactionTypeEnum,
  TradeTypeEnum,
  Transaction,
  TransactionFilter,
} from '@olv/ui-shared/models/merchant-transaction.model';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import { MerchantService } from '@olv/ui-shared/services/merchant.service';
import { WalletService } from '@olv/ui-shared/services/wallet.service';
import { PillSearchComponent, SearchState } from '@olv/ui-shared/ui-shared/pill-search/pill-search.component';
import { TransactionDetailsComponent } from '@olv/ui-shared/ui-shared/transaction-details/transaction-details.component';
import { TransactionsTableComponent } from '@olv/ui-shared/ui-shared/transactions-table/transactions-table.component';
import { SystemService } from '@olv/ui-shared/services/system.service';
import { ReportsService } from '@olv/ui-shared/services/reports.service';
import { ExportComponent, ExportDialogData } from '@olv/ui-shared/ui-shared/export/export.component';
import { DatePickerComponent } from '@olv/ui-shared/ui-shared/date-picker/date-picker.component';
import { AppliedFilter, FilterService } from '@olv/ui-shared/services/filter.service';
import { AppContextService } from '@olv/ui-shared/services/app-context.service';
import { DatePickerService, PresetPeriod, RangeOfDate } from '@olv/ui-shared/services/date-picker.service';
import { enumToFilterFields, splitPascalCase } from '@olv/ui-shared/utils/enum-to-filter.util';
import { MatDialog } from '@angular/material/dialog';
import { FiltersDialogComponent } from '@olv/ui-shared/ui-shared/filters-dialog/filters.component';
import { AuthService } from '@olv/ui-shared/services/auth.service';
import { ExchangeCategoryService } from '@olv/ui-shared/services/exchange-category.service';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';

export interface TransactionHistoryConfig {
  title?: string;
  showDetailsPanel?: boolean;
  filterOptions?: {
    actionRequired?: string[];
    type?: string[];
    account?: string[];
  };
}

export interface TransactionHistoryService<T extends Transaction = Transaction> {
  getTransactions(
    entityId: string,
    filter: TransactionFilter,
  ): Observable<{
    items: T[];
    totalCount: number;
  }>;
  openSidebar?(): void;
  closeSidebar?(): void;
}

@Component({
  selector: 'app-transaction-history',
  standalone: true,
  imports: [
    MatTooltipModule,
    PillSearchComponent,
    TransactionsTableComponent,
    TransactionDetailsComponent,
    HasPermissionDirective,
    RefreshSpinDirective,
    MatIconModule,
    DatePickerComponent,
    ButtonComponent,
  ],
  templateUrl: './transaction-history.component.html',
  styleUrls: ['./transaction-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class TransactionHistoryComponent<T extends Transaction = Transaction> implements OnInit, OnDestroy {
  private router = inject(Router);
  private systemService = inject(SystemService);
  private cdr = inject(ChangeDetectorRef);
  private merchantService = inject(MerchantService);
  private reportsService = inject(ReportsService);
  private walletService = inject(WalletService);

  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;
  @Input() entityId = '';
  @Input() config: TransactionHistoryConfig = {};
  @Input() transactionService!: TransactionHistoryService<T>;
  @Input() type = '';
  /**
   * When true, the "Exchange" entry is removed from the Transaction Type
   * filter dropdown. Callers should set this explicitly (e.g. reports and
   * wallet-detail routes) rather than relying on router URL inspection.
   */
  @Input() hideExchangeType = false;
  @Input() initialSearch = '';
  private autoOpenTxId: string | null = null;
  private pendingAutoOpen: T | null = null;
  private pendingAutoOpenTimer: ReturnType<typeof setTimeout> | null = null;

  @Output() transactionSelected = new EventEmitter<T>();
  @Output() transactionUpdated = new EventEmitter<string>();
  @Output() loadingChange = new EventEmitter<boolean>();

  @ViewChild('sidebarRef') sidebarRef!: ElementRef;

  private datePickerService = inject(DatePickerService);
  private filterService = inject(FilterService);
  private readonly appCtx = inject(AppContextService);
  private dialog = inject(MatDialog);
  private auth = inject(AuthService);
  private readonly exchangeCategoryService = inject(ExchangeCategoryService);
  private readonly metadata = inject(NetworkMetadataService);

  isMerchantApp = false;

  get title(): string {
    return this.config.title || 'Transaction History';
  }

  get showDetailsPanel(): boolean {
    return this.config.showDetailsPanel !== false;
  }

  transactions: T[] = [];

  openDropdown: string | null = null;

  selectedActionRequired = 'All';
  selectedType = 'All';
  selectedAccount = 'All';
  searchText = '';
  exchangeCategoryOptions: { value: number; label: string }[] = [];
  filterMap: Partial<TransactionFilter> = {};
  filtersApplied = false;

  sortColumn = 'date';
  sortDirection: 'asc' | 'desc' = 'desc';

  get hasActiveFilters(): boolean {
    return (
      this.selectedActionRequired !== 'All' ||
      this.selectedType !== 'All' ||
      this.selectedAccount !== 'All' ||
      this.searchText.trim() !== ''
    );
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.selectedActionRequired !== 'All') count++;
    if (this.selectedType !== 'All') count++;
    if (this.selectedAccount !== 'All') count++;
    if (this.searchText.trim() !== '') count++;
    return count;
  }

  pageSize = 10;
  currentSkip = 0;
  totalCount = 0;
  hasMoreTransactions = false;

  isLoading = false;
  initialLoading = true;
  isFirstLoad = true;
  isDisableFilters = false;
  hasExportData = false;

  get exportButtonState(): 'disabled' | 'default' {
    return this.hasExportData ? 'default' : 'disabled';
  }

  error: string | null = null;

  private destroy$ = new Subject<void>();

  selectedTransaction: T | null = null;
  sideComponentOpen = false;
  selectedPeriod = this.datePickerService.DEFAULT_DATE_PERIOD;
  merchantOptions: { value: string; label: string }[] = [];
  public showReports = false;

  ngOnInit(): void {
    this.metadata.load();
    this.showReports = this.router.url.startsWith('/reports');
    const appName = this.appCtx.getAppName();
    this.isMerchantApp = appName === 'merchant';
    this.initializeComponent();
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }
  ngOnDestroy(): void {
    if (this.pendingAutoOpenTimer) {
      clearTimeout(this.pendingAutoOpenTimer);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeComponent(): void {
    if (this.initialSearch) {
      this.searchText = this.initialSearch;
      const ids = this.initialSearch
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      this.autoOpenTxId = ids.length === 1 ? ids[0] : null;
    }
    const { start, end } = this.datePickerService.setDefaultDate();
    this.filterMap.startDate = start.toISOString();
    this.filterMap.endDate = end.toISOString();
    this.loadExchangeCategories()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadTransactions(),
        error: () => this.loadTransactions(),
      });
  }

  onDropdownToggled(dropdownId: string): void {
    this.openDropdown = this.openDropdown === dropdownId ? null : dropdownId;
  }

  onOptionSelected(event: { id: string; value: string }): void {
    switch (event.id) {
      case 'actionRequired':
        this.selectedActionRequired = event.value;
        break;
      case 'type':
        this.selectedType = event.value;
        break;
      case 'account':
        this.selectedAccount = event.value;
        break;
    }
    this.openDropdown = null;
    this.applyFilters();
  }

  private loadMerchants$(): Observable<void> {
    return this.reportsService.getMerchantFilter().pipe(
      map((merchants) => {
        this.merchantOptions = merchants.map((m) => ({
          value: m.id,
          label: m.name,
        }));
      }),
    );
  }

  private resolveTransactions$(loadMore = false): Observable<{ items: T[]; totalCount: number }> {
    const filterWithSort = {
      ...this.filterMap,
      filter: this.searchText?.trim() || undefined,
      skipCount: loadMore ? this.currentSkip : 0,
      maxResultCount: this.pageSize,
      sorting: `${this.sortColumn} ${this.sortDirection}`,
    };

    const serviceType = this.getActiveService();

    if (serviceType === 'system') {
      return this.systemService
        .getSystemTransactions(filterWithSort)
        .pipe(map((r) => ({ items: r.items as T[], totalCount: r.totalCount })));
    }

    if (serviceType === 'reports') {
      return this.reportsService
        .getReportTransactions(filterWithSort)
        .pipe(map((r) => ({ items: r.items as T[], totalCount: r.totalCount })));
    }

    if (serviceType === 'wallet' && this.type === 'merchant') {
      return this.walletService
        .getMerchantWalletTransactions(this.entityId, filterWithSort)
        .pipe(map((r) => ({ items: r.items as T[], totalCount: r.totalCount })));
    }

    if (serviceType === 'wallet' && this.type === 'system') {
      return this.walletService
        .getSystemWalletTransactions(this.entityId, filterWithSort)
        .pipe(map((r) => ({ items: r.items as T[], totalCount: r.totalCount })));
    }

    const merchantIdToSend = this.isMerchantApp ? undefined : this.entityId;
    return this.merchantService
      .getMerchantTransactions(merchantIdToSend, this.isMerchantApp, filterWithSort)
      .pipe(map((r) => ({ items: r.items as T[], totalCount: r.totalCount })));
  }

  private applyTransactionsResult(result: { items: T[]; totalCount: number }, loadMore: boolean): void {
    this.transactions = loadMore ? [...this.transactions, ...result.items] : result.items;

    this.totalCount = result.totalCount;
    this.currentSkip += result.items.length;
    this.hasMoreTransactions = this.currentSkip < this.totalCount;
    this.hasExportData = this.transactions.length > 0;

    if (this.isFirstLoad) {
      this.isDisableFilters = this.transactions.length === 0 && !this.searchText?.trim();
    }

    if (this.autoOpenTxId && !loadMore) {
      this.pendingAutoOpen = this.transactions.find((t) => t.id === this.autoOpenTxId) ?? null;
      this.autoOpenTxId = null;
    }
  }

  private openPendingTransaction(): void {
    this.pendingAutoOpenTimer = null;
    const match = this.pendingAutoOpen;
    this.pendingAutoOpen = null;
    if (match) {
      this.showTransactionDetails(match);
    }
  }

  private schedulePendingTransactionOpen(): void {
    if (!this.pendingAutoOpen) {
      return;
    }

    if (this.pendingAutoOpenTimer) {
      clearTimeout(this.pendingAutoOpenTimer);
    }

    this.pendingAutoOpenTimer = setTimeout(() => this.openPendingTransaction(), 0);
  }

  private loadExchangeCategories(): Observable<void> {
    return this.exchangeCategoryService.getExchangeCategories().pipe(
      map((categories) => {
        const byId = new Map<number, string>();
        (categories ?? []).forEach((category) => {
          const label = category.label?.trim();
          if (label && !byId.has(category.id)) {
            byId.set(category.id, label);
          }
        });

        this.exchangeCategoryOptions = Array.from(byId, ([value, label]) => ({ value, label })).sort((a, b) =>
          a.label.localeCompare(b.label),
        );
      }),
    );
  }
  loadTransactions(loadMore = false): void {
    const isBlockingLoad = !loadMore;

    if (loadMore) {
      this.isLoading = true;
    } else {
      this.initialLoading = true;
      this.resetPagination();
    }
    if (isBlockingLoad) {
      this.loadingChange.emit(true);
    }

    const transactions$ = this.resolveTransactions$(loadMore);

    const canLoadMerchants =
      !this.isMerchantApp && this.showReports && this.auth.hasPermission(Resources.Reports, Scopes.MerchantsFilter);

    const stream$ = canLoadMerchants
      ? forkJoin({
          merchants: this.loadMerchants$(),
          transactions: transactions$,
        }).pipe(map((r) => r.transactions))
      : transactions$;

    stream$
      .pipe(
        takeUntil(this.destroy$),
        tap((result) => this.applyTransactionsResult(result, loadMore)),
        finalize(() => {
          this.isLoading = false;
          this.initialLoading = false;
          this.isFirstLoad = false;
          if (isBlockingLoad) {
            this.loadingChange.emit(false);
          }
          this.schedulePendingTransactionOpen();
        }),
      )
      .subscribe({
        error: () => {
          this.error = 'Failed to load transactions. Please try again.';
        },
      });
  }

  loadMoreTransactions(): void {
    if (!this.isLoading && this.hasMoreTransactions) {
      this.loadTransactions(true);
    }
  }

  onFiltersApplied(values: AppliedFilter[]) {
    this.filtersApplied = values.length > 0;

    const { startDate, endDate } = this.filterMap;

    this.filterMap = {
      startDate: startDate,
      endDate: endDate,
      ...this.filterService.applySelectedFilters(values, {}),
    };

    this.resetPagination();
    this.loadTransactions();
  }

  applyFilters(): void {
    this.currentSkip = 0;
  }

  updateSearch(searchState: SearchState): void {
    if (this.searchText === searchState.value) {
      return;
    }

    this.searchText = searchState.value;
    this.resetPagination();
    this.loadTransactions();
  }

  sortBy(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      if (column === 'amount') {
        this.sortDirection = 'desc';
      } else if (column === 'date') {
        this.sortDirection = 'desc';
      } else {
        this.sortDirection = 'asc';
      }
    }

    this.applyFilters();
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) {
      return 'unfold_more';
    }
    return this.sortDirection === 'asc' ? 'keyboard_arrow_up' : 'keyboard_arrow_down';
  }

  isSortedBy(column: string): boolean {
    return this.sortColumn === column;
  }

  get hasTransactions(): boolean {
    return this.transactions.length > 0;
  }

  showTransactionDetails(transaction: Transaction): void {
    const clickedOnSame = this.selectedTransaction === transaction;
    this.transactionSelected.emit(transaction as T);
    if (clickedOnSame) {
      this.closeTransactionDetails();
    } else {
      setTimeout(() => {
        this.merchantService.openSidebar();
        this.selectedTransaction = transaction as T;
      }, 0);
    }
  }

  closeTransactionDetails(): void {
    this.selectedTransaction = null;
    this.merchantService.closeSidebar();
  }

  onTransactionUpdated(transactionId: string): void {
    this.transactionUpdated.emit(transactionId);

    this.loadTransactions();
  }
  toggleFilters(): void {
    this.metadata.whenLoaded().subscribe(() => this.openFilters());
  }

  private openFilters(): void {
    this.selectedTransaction = null;
    this.merchantService.closeSidebar();

    const dropdownsOptions =
      this.exchangeCategoryOptions.length > 0
        ? [
            {
              key: 'exchangeCategories',
              label: 'Exchange Category',
              placeholder: 'Select Exchange Categories',
              options: this.exchangeCategoryOptions,
              type: 'checkbox',
              isSearch: true,
              isFilterClass: true,
              afterFilter: 'Risk Level',
            },
          ]
        : [];

    const merchantDropdown =
      this.showReports && this.auth.hasPermission(Resources.Reports, Scopes.MerchantsFilter)
        ? [
            {
              key: 'merchantIds',
              label: 'Merchants',
              placeholder: 'Select Merchants',
              options: this.merchantOptions,
              type: 'checkbox',
              isSearch: true,
              isFilterClass: true,
            },
          ]
        : [];

    const dialogRef = this.dialog.open(FiltersDialogComponent, {
      width: '600px',
      maxHeight: '95vh',
      maxWidth: '95vw',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        filters: this.filters,
        dropdownsOptions: [...dropdownsOptions, ...merchantDropdown],
        preselectedValues: this.getAppliedFiltersAsArray(),
        isDisableFilters: this.isDisabled,
      },
    });

    const sub = dialogRef.componentInstance.resetRequest.subscribe((result) => {
      this.onFiltersApplied(result);
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.onFiltersApplied(result);
      }

      sub.unsubscribe();
    });
  }
  openExport(): void {
    this.selectedTransaction = null;
    this.merchantService.closeSidebar();

    this.dialog.open<ExportComponent, ExportDialogData>(ExportComponent, {
      disableClose: false,
      autoFocus: false,
      maxWidth: '95vw',
      maxHeight: '95vh',
      panelClass: 'custom-dialog-container',
      data: {
        tabName: 'transaction',
        trancationFilters: this.filterMap,
      },
    });
  }
  refreshDetails(): void {
    this.loadTransactions();
    if (this.router.url.startsWith('/wallet-detail')) {
      this.merchantService.triggerWalletDetailRefresh();
    }

    if (this.router.url.startsWith('/dashboard')) {
      this.systemService.triggerDashboardRefresh();
    }

    if (this.router.url.startsWith('/merchants')) {
      this.merchantService.triggerMerchantDetailRefresh();
    }
  }

  getAppliedFiltersAsArray() {
    return this.filterService.getAppliedFilters(this.filterMap);
  }

  private getActiveService(): 'system' | 'reports' | 'merchant' | 'wallet' {
    if (this.router.url.startsWith('/dashboard') && !this.isMerchantApp) return 'system';
    if (this.router.url.startsWith('/wallet-detail')) return 'wallet';
    if (this.router.url.startsWith('/reports')) return 'reports';
    return 'merchant';
  }
  get sidebarOpen() {
    return this.merchantService.sidebarOpenSignal();
  }
  onFilterReset(): void {
    const { startDate, endDate } = this.filterMap;

    this.filterMap = {
      startDate: startDate,
      endDate: endDate,
    };
    this.filtersApplied = false;

    this.resetPagination();
    this.loadTransactions();
  }

  onPresetPeriod(preset: string): void {
    const range = this.datePickerService.presetPeriod(preset as PresetPeriod);
    this.selectedPeriod = this.datePickerService.selectedPeriod;

    this.filterMap.startDate = range.start.toISOString();
    this.filterMap.endDate = range.end.toISOString();

    this.resetPagination();
    this.loadTransactions();
  }

  onDateRangeSelected(range: RangeOfDate): void {
    const newRange = this.datePickerService.dateRangeSelected(range);
    this.selectedPeriod = this.datePickerService.selectedPeriod;

    this.filterMap.startDate = newRange.start.toISOString();
    this.filterMap.endDate = newRange.end.toISOString();

    this.resetPagination();
    this.loadTransactions();
  }
  get isDisabled(): boolean {
    if (this.isFirstLoad) {
      return this.initialLoading || this.isLoading;
    }

    return this.isDisableFilters;
  }
  private resetPagination(): void {
    this.currentSkip = 0;
    this.transactions = [];
  }

  private getTransactionTypeEnum() {
    if (this.isMerchantApp) {
      return {
        Deposit: TransactionTypeEnum.Deposit,
        Withdrawal: TransactionTypeEnum.Withdrawal,
      };
    }

    if (this.hideExchangeType) {
      const { Exchange, ...rest } = TransactionTypeEnum;
      return rest;
    }

    return TransactionTypeEnum;
  }

  get filters() {
    const filters = [];

    if (!this.isMerchantApp) {
      filters.push({
        name: 'Risk Level',
        filterFields: enumToFilterFields(RiskLevelEnum),
      });
    }

    if (!this.router.url.startsWith('/wallet-detail')) {
      filters.push({
        name: 'Blockchain',
        filterFields: this.metadata.filterFields(),
      });
    }

    const tradeTypeFilterFields = [{ name: 'All', value: 0 }, ...enumToFilterFields(TradeTypeEnum)];

    filters.push(
      {
        name: 'Status',
        filterFields: enumToFilterFields(TransactionStatusEnum),
      },
      {
        name: 'Transaction Type',
        filterFields: enumToFilterFields(this.getTransactionTypeEnum(), splitPascalCase),
      },
      ...(this.router.url.startsWith('/dashboard') && !this.isMerchantApp
        ? [
            {
              name: 'Trade Type',
              filterFields: tradeTypeFilterFields,
            },
          ]
        : []),
      {
        name: 'Action Required',
        filterFields: [
          { name: 'Required', value: 1 },
          { name: 'Not Required', value: 0 },
        ],
      },
      ...(this.isMerchantApp
        ? []
        : [
            {
              name: 'Exchange Rate',
              filterFields: [{ name: 'During outage', value: 1 }],
            },
          ]),
    );

    return filters;
  }
}
